'use client';

import { create } from 'zustand';
import { useOverlayStore } from '@/shared/components/overlay/overlay.store';
import { emitGTMEvent } from '@/features/gtm/shared/helper/emit-gtm-event';
import { isWtGamesAppUserAgent } from '@/features/games-app-bridge/shared/helpers/is-wt-games-app-user-agent';
import {
  clearPendingGateAction,
  persistPendingGateAction,
  persistPendingGateMethod,
  type PendingGateAction,
} from '@/features/auth/shared/state/pending-gate-action';

export const AUTH_GATE_MODAL_KEY = 'auth-gate';

export type AuthGateView = 'login' | 'register' | 'verifySent' | 'success';
export type AuthGateOrigin = 'web' | 'client';
export type AuthGateTrigger = 'trial_download' | 'verification_return' | 'wishlist';
export type AuthGateMethod = string;
export type AuthGateMethodStatus = 'started' | 'succeeded';
export type AuthGateOutcome = 'completed' | 'dismissed';

/** Analytics name of a stored action's type, shared by both emitters of `AUTH_GATE_COMPLETED`. */
export const TRIGGER_BY_PENDING_ACTION: Record<PendingGateAction['type'], AuthGateTrigger> = {
  trialDownload: 'trial_download',
};

const trackAuthGateShown = ({
  trigger,
  view,
  sku,
}: {
  trigger: AuthGateTrigger;
  view: AuthGateView;
  sku: string | null;
}): void => {
  emitGTMEvent('AUTH_GATE_SHOWN', {
    auth_gate_trigger: trigger,
    auth_gate_view: view,
    auth_gate_sku: sku,
  });
};

const trackAuthGateMethod = ({
  method,
  status,
  trigger,
}: {
  method: AuthGateMethod;
  status: AuthGateMethodStatus;
  trigger: AuthGateTrigger | null;
}): void => {
  emitGTMEvent('AUTH_GATE_METHOD_CHOSEN', {
    auth_gate_method: method,
    auth_gate_status: status,
    auth_gate_trigger: trigger,
  });
};

export const trackAuthGateCompleted = ({
  trigger,
  resumed,
  method = null,
  outcome = 'completed',
}: {
  trigger: AuthGateTrigger | null;
  resumed: boolean;
  method?: AuthGateMethod | null;
  outcome?: AuthGateOutcome;
}): void => {
  emitGTMEvent('AUTH_GATE_COMPLETED', {
    auth_gate_trigger: trigger,
    auth_gate_resumed: resumed,
    auth_gate_method: method,
    auth_gate_outcome: outcome,
  });
};

export interface AuthGateOptions {
  view?: AuthGateView;
  prefillEmail?: string;
  hideSocial?: boolean;
  origin?: AuthGateOrigin;
  pendingAction?: () => void;
  redirectUrl?: string;
  resumeAfterRedirect?: PendingGateAction;
  trigger?: AuthGateTrigger;
}

const currentOrigin = (): AuthGateOrigin => {
  if (typeof navigator === 'undefined') return 'web';
  return isWtGamesAppUserAgent(navigator.userAgent) ? 'client' : 'web';
};

const currentLocation = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}`;
};

interface AuthGateState extends Required<Pick<AuthGateOptions, 'view' | 'hideSocial' | 'origin'>> {
  prefillEmail: string;
  trigger: AuthGateTrigger | null;
  redirectUrl: string;
  pendingAction: (() => void) | null;
  method: AuthGateMethod | null;
  resolved: boolean;
  claims: boolean;
  /**
   * Held in memory until the user commits to a flow that leaves the page. Persisting on open
   * would leave a live entry behind after a reload and resume a download nobody asked for.
   */
  resumable: PendingGateAction | null;
  setView: (view: AuthGateView) => void;
  setPrefillEmail: (email: string) => void;
  setMethod: (method: AuthGateMethod) => void;
  markResolved: () => void;
  reset: () => void;
}

const initialState = {
  view: 'login' as AuthGateView,
  prefillEmail: '',
  hideSocial: false,
  origin: 'web' as AuthGateOrigin,
  redirectUrl: '',
  pendingAction: null,
  trigger: null,
  method: null,
  resolved: false,
  claims: false,
  resumable: null,
};

export const useAuthGateStore = create<AuthGateState>((set) => ({
  ...initialState,
  setView: (view) => set({ view }),
  setPrefillEmail: (prefillEmail) => set({ prefillEmail }),
  setMethod: (method) => set({ method }),
  markResolved: () => set({ resolved: true }),
  reset: () => set(initialState),
}));

/** Continues in the user's inbox, so it counts as leaving the page. */
export const EMAIL_SIGNUP_METHOD = 'email_signup';

/**
 * Only flows that leave the page need a stored entry. A credential login finishes in this tab
 * and holds the action in memory; an entry could let a tab that lost its claim download twice.
 */
const leavesThePage = (method: AuthGateMethod, status: AuthGateMethodStatus): boolean =>
  status === 'started' || method === EMAIL_SIGNUP_METHOD;

/** The commit point: the first moment worth leaving a resumable entry behind. */
export const chooseAuthGateMethod = (
  method: AuthGateMethod,
  status: AuthGateMethodStatus,
): void => {
  const { setMethod, resumable } = useAuthGateStore.getState();
  setMethod(method);

  if (resumable && leavesThePage(method, status)) {
    useAuthGateStore.setState({ claims: persistPendingGateAction(resumable) });
  }
  persistPendingGateMethod(method);

  trackAuthGateMethod({ method, status, trigger: useAuthGateStore.getState().trigger });
};

export const openAuthGate = ({
  view = 'login',
  prefillEmail = '',
  hideSocial = false,
  origin,
  redirectUrl,
  pendingAction,
  resumeAfterRedirect,
  trigger,
}: AuthGateOptions = {}) => {
  const alreadyOpen = useOverlayStore.getState().activeModalKey === AUTH_GATE_MODAL_KEY;

  useAuthGateStore.setState({
    view,
    prefillEmail,
    hideSocial,
    origin: origin ?? currentOrigin(),
    redirectUrl: redirectUrl || currentLocation(),
    pendingAction: pendingAction ?? null,
    trigger: trigger ?? null,
    method: null,
    resolved: false,
    claims: false,
    resumable: resumeAfterRedirect ?? null,
  });

  if (trigger && !alreadyOpen) {
    trackAuthGateShown({ trigger, view, sku: resumeAfterRedirect?.sku ?? null });
  }

  // A fresh gate never inherits an older entry, except on a verification return, where the
  // stored entry is the whole point. `chooseAuthGateMethod` persists `resumeAfterRedirect` later.
  if (trigger && trigger !== 'verification_return') {
    clearPendingGateAction();
  }

  useOverlayStore.getState().showOverlay(AUTH_GATE_MODAL_KEY);
};

export const closeAuthGate = () => {
  const { activeModalKey, hideOverlay } = useOverlayStore.getState();

  if (activeModalKey === AUTH_GATE_MODAL_KEY) {
    hideOverlay();
  }

  const { trigger, method, resolved } = useAuthGateStore.getState();
  if (trigger && !resolved) {
    trackAuthGateCompleted({ trigger, resumed: false, method, outcome: 'dismissed' });
  }

  clearPendingGateAction();
  useAuthGateStore.getState().reset();
};
